import { AdvisorAvatar } from '../Common/AdvisorAvatar'
import { MessageSquare, Calendar, GitBranch } from 'lucide-react'

const STATUS_STYLES = {
  open:        { bg: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', dot: 'bg-gray-400' },
  in_progress: { bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', dot: 'bg-blue-500' },
  completed:   { bg: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', dot: 'bg-green-500' },
  cancelled:   { bg: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', dot: 'bg-red-400' },
}

const PRIORITY_STYLES = {
  high:   'text-red-500',
  medium: 'text-yellow-500',
  low:    'text-gray-400',
}

const SECTION_COLORS = {
  'Claims':               'bg-orange-100 text-orange-700',
  'Renewals':             'bg-purple-100 text-purple-700',
  'Amendments':           'bg-indigo-100 text-indigo-700',
  'Transfers':            'bg-cyan-100 text-cyan-700',
  'Portfolio Reviews':    'bg-teal-100 text-teal-700',
  'Sales & Onboarding':   'bg-green-100 text-green-700',
  'Client Admin':         'bg-blue-100 text-blue-700',
  'Quotes & Applications':'bg-yellow-100 text-yellow-700',
  'Will':                 'bg-pink-100 text-pink-700',
  'Medical Aid':          'bg-rose-100 text-rose-700',
  'Tax':                  'bg-lime-100 text-lime-700',
}

function formatDate(dt) {
  if (!dt) return null
  const d = new Date(dt)
  const today = new Date()
  today.setHours(0,0,0,0)
  d.setHours(0,0,0,0)
  const diff = d - today
  if (diff === 0) return { label: 'Today', overdue: false, today: true }
  if (diff === 86400000) return { label: 'Tomorrow', overdue: false, today: false }
  if (diff < 0) return { label: d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }), overdue: true, today: false }
  return { label: d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }), overdue: false, today: false }
}

// ── List row mode ─────────────────────────────────────────────────────────────
function ListRow({ task, onClick, isSelected, isDarkMode, subtaskCount = 0 }) {
  const status = STATUS_STYLES[task.status] ?? STATUS_STYLES.open
  const dateInfo = formatDate(task.due_date)
  const isCompleted = task.status === 'completed'

  const rowBg = isSelected
    ? isDarkMode ? 'bg-slate-700' : 'bg-sfp-lightblue'
    : isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors border-b ${
        isDarkMode ? 'border-slate-800' : 'border-gray-100'
      } ${rowBg}`}
    >
      {/* Status dot */}
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${status.dot}`} />

      {/* Task name */}
      <p className={`flex-1 text-sm min-w-0 truncate ${
        isCompleted ? 'line-through opacity-50' : ''
      } ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
        {task.claim_number || task.title}
      </p>

      {/* Client */}
      <p className={`hidden md:block text-xs w-36 truncate flex-shrink-0 ${
        isDarkMode ? 'text-slate-400' : 'text-gray-500'
      }`}>
        {task.client_name !== '—' ? task.client_name : ''}
      </p>

      {/* Due date */}
      <div className="w-24 flex-shrink-0">
        {dateInfo && (
          <span className={`flex items-center gap-1 text-xs ${
            dateInfo.overdue ? 'text-red-500' : dateInfo.today ? 'text-green-600' : isDarkMode ? 'text-slate-400' : 'text-gray-500'
          }`}>
            <Calendar className="w-3 h-3" />
            {dateInfo.label}
          </span>
        )}
      </div>

      {/* Status chip */}
      <span className={`hidden sm:inline px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${status.bg}`}>
        {task.status.replace('_', ' ')}
      </span>

      {/* Priority dot */}
      <span className={`text-xs font-bold flex-shrink-0 ${PRIORITY_STYLES[task.priority] ?? PRIORITY_STYLES.low}`}>
        {task.priority?.[0]?.toUpperCase() ?? 'L'}
      </span>

      {/* Assignee avatar */}
      <AdvisorAvatar
        avatarUrl={task.advisors?.avatar_url ?? null}
        fullName={task.advisors?.full_name ?? ''}
        size="sm"
      />

      {/* Subtask count badge */}
      {subtaskCount > 0 && (
        <span className={`flex items-center gap-0.5 text-xs flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
          <GitBranch className="w-3 h-3" />
          {subtaskCount}
        </span>
      )}

      {/* Comment count */}
      {task.comment_count > 0 && (
        <span className={`flex items-center gap-0.5 text-xs flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
          <MessageSquare className="w-3 h-3" />
          {task.comment_count}
        </span>
      )}
    </div>
  )
}

// ── Board card mode ───────────────────────────────────────────────────────────
function BoardCard({ task, onClick, isDarkMode }) {
  const dateInfo = formatDate(task.due_date)
  const sectionColor = SECTION_COLORS[task.section] ?? 'bg-gray-100 text-gray-600'
  const isCompleted = task.status === 'completed'

  return (
    <div
      onClick={onClick}
      className={`p-3 rounded-lg border cursor-pointer transition-shadow hover:shadow-md ${
        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
      }`}
    >
      <p className={`text-sm font-medium mb-1 ${
        isCompleted ? 'line-through opacity-50' : isDarkMode ? 'text-slate-100' : 'text-gray-800'
      }`}>
        {task.claim_number || task.title}
      </p>
      {task.client_name && task.client_name !== '—' && (
        <p className={`text-xs mb-2 truncate ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
          {task.client_name}
        </p>
      )}
      <div className="flex items-center justify-between gap-2 mt-2">
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${sectionColor}`}>
          {task.section}
        </span>
        <div className="flex items-center gap-2">
          {dateInfo && (
            <span className={`text-xs ${dateInfo.overdue ? 'text-red-500' : isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
              {dateInfo.label}
            </span>
          )}
          {task.comment_count > 0 && (
            <span className={`flex items-center gap-0.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
              <MessageSquare className="w-3 h-3" />
              {task.comment_count}
            </span>
          )}
          <AdvisorAvatar
            avatarUrl={task.advisors?.avatar_url ?? null}
            fullName={task.advisors?.full_name ?? ''}
            size="sm"
          />
        </div>
      </div>
    </div>
  )
}

export function TaskCard({ task, mode = 'list', onClick, isSelected = false, isDarkMode = false, subtaskCount = 0 }) {
  if (mode === 'board') return <BoardCard task={task} onClick={onClick} isDarkMode={isDarkMode} />
  return <ListRow task={task} onClick={onClick} isSelected={isSelected} isDarkMode={isDarkMode} subtaskCount={subtaskCount} />
}

export { SECTION_COLORS, STATUS_STYLES }
